import React, { useState, useEffect } from 'react';
import './App.css';
import profileImage from './elliot_portrait.jpeg';

function App() {
  const [activeSection, setActiveSection] = useState('home');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'projects', 'experience', 'resume', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && scrollPosition >= element.offsetTop) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const projects = [
    {
      title: "Streaming Service Platform",
      description: "Full Stack Media App collaborating in an Agile team of 4 as Scrum Master to build a scalable media streaming platform with user authentication and a dynamic catalog. Designing secure architecture, features using Django, React, and AWS S3/RDS.",
      techStack: ["React JS", "Django", "AWS S3", "RDS", "Agile/Scrum", "Docker", "Airflow"],
      github: "https://github.com/elliotSchmango/Streaming-Service",
      status: "In Progress, Expected August 2025"
    },
    {
      title: "Dermadvisor - Mobile Skin Condition Classifier",
      description: "Designed and developed a cross-platform mobile app using React Native to classify escalated skin images using a ResNet18 model hosted on Amazon (AWS) SageMaker. Integrated with device and gallery with Expo; sent photos through an Express backend for real-time prediction. Displayed skin condition descriptions, sample images, and APA-style citations dynamically using content stored in AWS DynamoDB and S3.",
      techStack: ["React Native", "ResNet18", "AWS SageMaker", "Express", "AWS DynamoDB", "AWS S3", "AWS API Gateway", "AWS Lambda"],
      github: "https://github.com/elliotSchmango/Dermadvisor",
      status: "Completed May 2025"
    },
    {
      title: "Virginia Population Analysis",
      description: "Machine learning project that predicts annual population estimates across Virginia using demographic data. Implemented multiple ML models including Linear Regression (baseline), Random Forest Regressor, and LSTM Neural Network with comprehensive feature engineering including one-hot encoding, scaling, and feature selection. Evaluated performance using 5-fold cross-validation with RMSE and R² metrics.",
      techStack: ["Python", "Jupyter Notebook", "Machine Learning", "LSTM", "Random Forest", "Scikit-learn"],
      github: "https://github.com/elliotSchmango/Virginia-Population-Analysis",
      status: "Completed April 2025"
    },
    {
      title: "Tech CLA - Catalog & Lending App",
      description: "Conducted stakeholder interviews to elicit user requirements and system constraints, ensuring the app addressed real-world use cases and facilitated weekly Agile meetings to track sprint progress and tackle impediments. Engineered core functionality using Django and Bootstrap for the backend and UI design. Hosted static assets on Amazon S3 and deployed the backend to Heroku, streamlining CI/CD pipelines using GitHub Actions.",
      techStack: ["Django", "Bootstrap", "Amazon S3", "Heroku", "GitHub Actions"],
      github: "https://github.com/elliotSchmango/tech-lending-app",
      status: "Completed April 2025"
    }
  ];

  const experiences = [
    {
      title: "Full-Stack Developer",
      company: "Hampton Roads Sanitation District",
      location: "Virginia Beach, VA",
      period: "June 2024 - Present",
      type: "Intern → Part Time → Intern",
      responsibilities: [
        "I first joined HRSD in the summer of 2024 to lead the development of a secure, multimodal RAG (Retrieval-Augmented Generation) assistant designed to support water plant engineers and technicians across 20+ municipalities in Virginia. The system integrated chart, image, and PDF interpretation using LLaMA 3.2, Amazon Textract, and BLIP-2, with a custom React and Django REST interface. Over the course of the academic year, I was retained part-time while managing a full UVA course load, ultimately completing and delivering a robust prototype that saved HRSD an estimated $120,000 in professional consulting/vendor costs.",
        "In summer 2025, I returned to HRSD to launch a new edge computing initiative—building a real-time, on-device computer vision system for monitoring industrial equipment anomalies. This work involved deploying models to Raspberry Pi-class devices, prioritizing low-latency performance and hardware constraints in field conditions where cloud connectivity is limited and security is critical."
      ]
    },
    {
      title: "IT Intern",
      company: "Save the Children International",
      location: "Phnom Penh, Cambodia",
      period: "December 2024 - January 2025",
      responsibilities: [
        "During my winter term in 2024, I interned with Save the Children in Phnom Penh, Cambodia, where I focused on building tools to support international development operations. I independently led the development of an HR dashboard using React, Django, and MongoDB, applying a bipartite matching-inspired algorithm to optimize internal project assignments based on staffing availability, budget constraints, and hiring status.",
        "Alongside this technical work, I also proposed and organized the organization’s first AI ethics workshop in Cambodia—an initiative I wanted to introduce non-technical staff to the implications of artificial intelligence in humanitarian work. The event reached over 100 staff across four satellite offices nationwide and helped facilitate dialogue about ethical implementation of emerging technologies in developing countries."
      ]      
    },
    {
      title: "Undergraduate Research Assistant",
      company: "University of Virginia, Department of Computer Science",
      location: "Charlottesville, VA",
      period: "September 2023 - Present",
      responsibilities: [
        "Since fall 2023, I’ve worked as an undergraduate research assistant in the Department of Computer Science at UVA, where I co-developed ASCI—an intelligent office hours platform used by over 2,000 CS students. The system integrates a React frontend with TitanX and Kyt0s backends, enabling students to access a streamlined help queue and educational resources. My primary focus has been on integrating a machine learning chatbot trained on historical course FAQ and Piazza data from multiple semesters, designed to answer routine student questions efficiently. I also initiated and drafted our IRB proposal to begin collecting system usage data, with the goal of contributing to a research paper that evaluates the pedagogical impact of automated support in large-scale undergraduate education."
      ]
    }
  ];

  const extracurriculars = [
    {
      title: "Student Data Team",
      company: "University of Virginia, Business & AI Institute",
      location: "Charlottesville, VA",
      period: "February 2025 - Present",
      description: "Worked on a team building an AI-powered Retrieval-Augmented Generation (RAG) system operating on historical sales and artist data to support data-driven marketing and merchandise decisions for a leading e-commerce platform"
    },
    {
      title: "Teaching Assistant",
      company: "The Global Teaching Project",
      location: "Mississippi",
      period: "August 2024 - Present",
      description: "Remotely lecture full-sized classes twice a week in under-served communities within Mississippi's public school system, collaborating closely with primary teachers to create and adapt engaging, student-centered lesson plans and focusing on fostering an inclusive and supportive learning environment to meet diverse student needs"
    },
    {
      title: "Software Developer, Technology Committee",
      company: "University of Virginia, HooHacks",
      location: "Charlottesville, VA",
      period: "August 2023 - Present",
      description: "Jointly designed 'Hack-X' system with a team for 1000+ registered hackathon participants, building authentication and other features implementing Firebase database for participants to keep track of hackathon progress. Helped develop Discord Bot using VoiceFlow to streamline hackathon Q/A"
    }
  ];

  const skills = {
    languages: ["Python (PyTorch, TensorFlow, NumPy, Streamlit, Matplotlib)", "Java", "JavaScript", "HTML/CSS", "SQL", "C++"],
    frameworks: ["React", "Django", "Tailwind", "Bootstrap", "Firebase", "LlamaIndex", "LangChain"],
    cloud: ["AWS", "MongoDB", "Heroku", "GitHub Actions", "Airflow"],
    interests: ["Running", "Cooking", "Simulation Racing", "Weightlifting", "Swimming", "Music Production"]
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-primary-dark/95 backdrop-blur-sm z-50 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <span className="text-xl font-bold text-primary-dark">David "Elliot" Hong</span>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {['home', 'projects', 'experience', 'resume', 'contact'].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeSection === section
                        ? 'bg-accent-orange text-white'
                        : 'text-primary-dark hover:bg-accent-orange hover:text-primary-dark'
                    }`}
                  >
                    {section.charAt(0).toUpperCase() + section.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button className="text-primary-light hover:text-accent-orange">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Home Section */}
      <section id="home" className="min-h-screen flex items-center bg-gradient-to-br from-primary-dark to-primary-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-primary-dark">Hi, I'm</span>
              <br />
              <span className="text-accent-orange">David Hong</span> 
              </h1>
              <p className="text-xl md:text-2xl text-primary-dark mb-8">
                (But you can call me Elliot)
              </p>
              <p className="text-lg text-blue mb-8 leading-relaxed">
                I'm a computer science student at UVA Engineering with a strong interest in data analytics (particularly machine learning), 
                cloud-based solutions, and building software tools that help people. I'm currently working as a Full-Stack Developer intern 
                with experience in React, Django, AWS, and multimodal AI systems.
              </p>
              <div className="text-blue mb-6">
                <h3 className="text-xl font-semibold text-accent-orange mb-3">Current Academic & Career Interests</h3>
                <ul className="text-base space-y-1 mb-4 text-blue">
                  <li>• Machine Learning & Deep Learning (esp. LLMs & analytics)</li>
                  <li>• Mobile/Webdev development with React Native/Expo, React JS, Django, Flask</li>
                  <li>• Cloud engineering (AWS/GCP)</li>
                  <li>• Research & applied AI in finance, education, environment, and health</li>
                </ul>
                <h3 className="text-xl font-semibold text-accent-orange mb-3">Hobbies & Personal Interests</h3>
                <ul className="text-base space-y-1 text-blue">
                  <li>• Weightlifting & golf ⛳</li>
                  <li>• Motorsports & sim racing 🏎️</li>
                  <li>• Cooking</li>
                </ul>
              </div>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection('resume')}
                  className="border-2 border-accent-orange text-accent-orange hover:bg-accent-orange hover:text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  View Resume
                </button>
                <button 
                  onClick={() => scrollToSection('projects')}
                  className="border-2 border-accent-orange text-accent-orange hover:bg-accent-orange hover:text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  See Projects
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="border-2 border-accent-orange text-accent-orange hover:bg-accent-orange hover:text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Contact Me
                </button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-80 h-80 rounded-full overflow-hidden shadow-2xl border-4 border-accent-orange">
                <img 
                  src={profileImage} 
                  alt="David Hong" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-primary-dark mb-16 text-center">Featured Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg border border-primary-light/20 hover:shadow-xl transition-shadow duration-300">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary-dark mb-3">{project.title}</h3>
                  <p className="text-primary-dark mb-4 text-sm leading-relaxed">{project.description}</p>
                  <div className="mb-4">
                    <h4 className="font-semibold text-primary-dark mb-2">Tech Stack:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.map((tech, techIndex) => (
                        <span key={techIndex} className="bg-accent-light text-accent-orange px-2 py-1 rounded text-xs font-medium">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-primary-dark mb-4 italic">{project.status}</p>
                  <a 
                    href={project.github} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-accent-orange hover:text-orange-600 font-medium"
                  >
                    View on GitHub
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-primary-dark mb-16 text-center">Professional Experience</h2>
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-accent-orange">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-primary-dark">{exp.title}</h3>
                    <p className="text-lg text-accent-orange font-medium">{exp.company}</p>
                    <p className="text-primary-dark">{exp.location}</p>
                  </div>
                  <div className="text-right mt-2 md:mt-0">
                    <p className="text-primary-dark font-medium">{exp.period}</p>
                    {exp.type && <p className="text-sm text-primary-dark">{exp.type}</p>}
                  </div>
                </div>
                {exp.responsibilities.map((paragraph, i) => (
                  <p key={i} className="text-sm text-primary-dark leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}
          </div>

          {/* Extracurricular Activities */}
          <h3 className="text-3xl font-bold text-primary-dark mb-12 text-center mt-16">Other Experiences & Activities</h3>
          <div className="space-y-6">
            {extracurriculars.map((activity, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-primary-light">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-primary-dark">{activity.title}</h4>
                    <p className="text-accent-orange font-medium">{activity.company}</p>
                    <p className="text-primary-dark text-sm">{activity.location}</p>
                  </div>
                  <p className="text-primary-dark font-medium mt-2 md:mt-0">{activity.period}</p>
                </div>
                <p className="text-primary-dark text-sm leading-relaxed">{activity.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resume Section */}
      <section id="resume" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-primary-dark mb-16 text-center">Resume Excerpt</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Skills */}
            <div>
              <h3 className="text-2xl font-bold text-primary-dark mb-6">Technical Skills</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-accent-orange mb-3">Languages</h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.languages.map((lang, index) => (
                      <span key={index} className="bg-accent-light text-accent-orange px-3 py-1 rounded-full text-sm">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-accent-orange mb-3">Frameworks & Libraries</h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.frameworks.map((framework, index) => (
                      <span key={index} className="bg-accent-light text-accent-orange px-3 py-1 rounded-full text-sm">
                        {framework}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-accent-orange mb-3">Cloud & Tools</h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.cloud.map((tool, index) => (
                      <span key={index} className="bg-accent-light text-accent-orange px-3 py-1 rounded-full text-sm">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-accent-orange mb-3">Interests</h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.interests.map((interest, index) => (
                      <span key={index} className="bg-accent-light text-accent-orange px-3 py-1 rounded-full text-sm">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Education */}
            <div>
              <h3 className="text-2xl font-bold text-primary-dark mb-6">Education</h3>
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h4 className="text-xl font-bold text-primary-dark">University of Virginia</h4>
                <p className="text-accent-orange font-medium">B.S. Computer Science</p>
                <p className="text-primary-dark">Charlottesville, VA</p>
                <p className="text-primary-dark font-medium mt-2">GPA: 3.8/4.0 | Expected May 2026</p>
                <p className="text-sm text-primary-dark mt-3">
                  <strong>Relevant Coursework:</strong> Algorithms, Risk Analysis, NLP, Systems/Organization, Theory, 
                  Software Engineering, Cybersecurity, Programming Languages
                </p>
                <p className="text-sm text-primary-dark mt-2">
                  <strong>Activities & Societies:</strong> Undergraduate CS Research, HooHacks, Business and AI Institute, 
                  Global Teaching Project, Trigon Engineering Society, Phi Society
                </p>
              </div>

              {/* Download Resume */}
              <div className="text-center">
                <a 
                  href="/Hong_David_Resume_2026.pdf" 
                  download= "David_Hong_Resume_2026.pdf"
                  className="bg-accent-orange hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-medium text-lg transition-colors inline-flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF Resume
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-primary-dark">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-16 text-center">Get In Touch</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="w-16 h-16 bg-accent-orange rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Email</h3>
              <a href="mailto:d.elliot.hong@gmail.com" className="text-primary-light hover:text-accent-orange transition-colors">
                d.elliot.hong@gmail.com
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="w-16 h-16 bg-accent-orange rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">LinkedIn</h3>
              <a href="https://www.linkedin.com/in/davidliolthong" target="_blank" rel="noopener noreferrer" className="text-primary-light hover:text-accent-orange transition-colors">
                /in/david-elliot-hong
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="w-16 h-16 bg-accent-orange rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">GitHub</h3>
              <a href="https://www.github.com/ElliotSchmango" target="_blank" rel="noopener noreferrer" className="text-primary-light hover:text-accent-orange transition-colors">
                /elliotSchmango
              </a>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-white">
              Let's connect and discuss opportunities!
            </p>
            <p className="text-primary-light/80 mt-2 text-white">
              📍 Currently based in the Washington DC/Metro Area
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            © 2025 David Hong. Built with React, Tailwind CSS, Emergent.sh, and Github Pages.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;